import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API_BASE_URL from '../../config/api';

const AllCourses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedMedium, setSelectedMedium] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_courses.php`);
        const result = await response.json();
        if (result.status === 'success') setCourses(result.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const teachers = [...new Set(courses.map(c => 
    c.teacher_name?.trim().toLowerCase()
  ))].filter(Boolean).map(name => 
    name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  );

  const filteredCourses = courses.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(search.toLowerCase()) ||
                         c.teacher_name?.toLowerCase().includes(search.toLowerCase());
    const matchesTeacher = selectedTeacher === '' || 
                         c.teacher_name?.trim().toLowerCase() === selectedTeacher.toLowerCase();
    const matchesMedium = selectedMedium === '' || c.medium === selectedMedium;
    return matchesSearch && matchesTeacher && matchesMedium;
  });

  return (
    <DashboardLayout title="Course Catalog">
      <div className="space-y-4 sm:space-y-6">

        {/* Header & Filters */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-sm sm:text-xl font-black text-slate-800 tracking-tight">Available Classes</h2>
              <p className="text-[10px] sm:text-xs text-slate-400 font-medium">Select a class to see monthly enrollment options.</p>
            </div>
            <div className="relative w-full sm:w-72">
              <input
                type="text"
                placeholder="Search class titles..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-5 py-3 bg-white border border-slate-100 rounded-2xl text-xs outline-none focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 transition-all shadow-sm pl-10"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Filter Bar - Increased Size */}
          <div className="flex flex-wrap items-center gap-4 bg-slate-50 p-3 rounded-[2rem] border border-slate-100">
            <div className="flex items-center gap-3 px-5 py-2.5 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.15em]">Teacher:</span>
              <select 
                className="bg-transparent text-xs sm:text-sm font-bold text-slate-700 outline-none cursor-pointer pr-2"
                value={selectedTeacher}
                onChange={(e) => setSelectedTeacher(e.target.value)}
              >
                <option value="">All Teachers</option>
                {teachers.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="flex items-center gap-3 px-5 py-2.5 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.15em]">Medium:</span>
              <select 
                className="bg-transparent text-xs sm:text-sm font-bold text-slate-700 outline-none cursor-pointer pr-2"
                value={selectedMedium}
                onChange={(e) => setSelectedMedium(e.target.value)}
              >
                <option value="">All Mediums</option>
                <option value="Sinhala">Sinhala</option>
                <option value="English">English</option>
              </select>
            </div>

            {(selectedTeacher || selectedMedium || search) && (
              <button 
                onClick={() => { setSelectedTeacher(''); setSelectedMedium(''); setSearch(''); }}
                className="text-[11px] font-black text-orange-500 uppercase tracking-widest hover:bg-orange-50 px-5 py-3 rounded-2xl transition-all"
              >
                Reset Filters
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {filteredCourses.map((course) => (
              <div
                key={course.id}
                onClick={() => navigate(`/student/course-months/${course.id}`)}
                className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group flex flex-col"
              >
                <div className={`h-36 sm:h-48 ${course.bg_pattern || 'bg-gradient-to-br from-blue-600 to-indigo-800'} relative flex items-center justify-center p-6 text-center overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="absolute top-4 right-4 z-20">
                    <span className="px-3 py-1.5 bg-white/20 backdrop-blur-md text-white text-[10px] font-black rounded-full uppercase tracking-widest border border-white/30">
                      {course.medium || 'Sinhala'}
                    </span>
                  </div>
                  <h3 className="text-white font-sinhala font-black text-lg sm:text-2xl drop-shadow-2xl z-10 leading-tight transform group-hover:scale-105 transition-transform duration-500">{course.title}</h3>
                </div>

                <div className="p-5 sm:p-6 space-y-4 flex-grow font-sinhala">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 font-bold border border-slate-100 text-sm shadow-inner group-hover:bg-orange-500 group-hover:text-white transition-colors duration-500">
                      {course.teacher_name?.charAt(0) || 'T'}
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-1">Assigned Teacher</p>
                      <p className="font-black text-slate-800 text-xs sm:text-sm uppercase tracking-tight">{course.teacher_name || 'Kasun Walakumbura'}</p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t border-slate-50">
                    <div className="flex gap-2">
                      <span className="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black rounded-full uppercase tracking-widest">
                        {course.tag || 'Theory'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none mb-1">Monthly Fee</p>
                      <span className="font-sans font-black text-slate-900 text-base sm:text-xl">LKR {course.price}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filteredCourses.length === 0 && (
          <div className="py-24 text-center space-y-4 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto text-slate-200 text-4xl shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 9.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="space-y-1">
              <h3 className="text-slate-800 font-bold">No results found</h3>
              <p className="text-slate-400 text-sm max-w-xs mx-auto">We couldn't find any classes matching your current filter criteria.</p>
            </div>
            <button onClick={() => { setSelectedTeacher(''); setSelectedMedium(''); setSearch(''); }} className="text-orange-500 font-black uppercase text-[10px] tracking-widest hover:underline px-6 py-2 bg-white rounded-full shadow-sm">Clear all filters</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AllCourses;
